import { useCallback, useEffect, useState } from "react";
import { ComingSoon } from "../../components/ComingSoon.js";
import { PageSEOSection } from "../../components/PageSEOSection.js";
import { usePageContext } from "vike-react/usePageContext";
import { getLanguageContent } from "../../content/index.js";
import { recordVocabularyAttempt } from "../../utils/progress.js";
import { MotifBand, motifLabel } from "../../components/Motifs.js";
import { langCodeToName } from "../../utils/appTheme.js";

const C = {
  bg: 'var(--bg)', surf: 'var(--surf)', surf2: 'var(--surf2)',
  border: 'var(--border)', border2: 'var(--border2)',
  text: 'var(--text)', muted: 'var(--muted)', dim: 'var(--dim)',
  accent: 'var(--accent)', accentBg: 'var(--accent-bg)', accentBorder: 'var(--accent-border)',
  greenBg: 'var(--success-bg)', greenBorder: 'var(--success-border)',
  redBg: 'var(--error-bg)', redBorder: 'var(--error-border)',
  amberBg: 'var(--warn-bg)', amberBorder: 'var(--warn-border)',
  blue: 'var(--blue)', blueBg: 'var(--blue-bg)',
}
const AF = {
  h: 'var(--font-h, "Space Grotesk", system-ui, sans-serif)',
  b: "'DM Sans', system-ui, sans-serif",
  m: "'DM Mono', 'Courier New', monospace",
}

const LANG_NAMES: Record<string, string> = {
  es: 'spanish', pt: 'portuguese', fi: 'finnish',
  fr: 'french', it: 'italian', ca: 'catalan', ro: 'romanian',
}

type VocabCard = { term: string; meaning: string; level: string }

// ── Shared exercise primitives (same as the grammar drill) ───────────────────
function Kbd({ children }: { children: string }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: AF.m, fontSize: 10, color: C.muted,
      background: C.surf2, border: `1px solid ${C.border}`,
      borderBottomWidth: 2, padding: '1px 5px', borderRadius: 4,
      lineHeight: 1.5, userSelect: 'none', whiteSpace: 'nowrap', flexShrink: 0,
    }}>{children}</span>
  )
}

function CounterBadge({ current, total }: { current: number; total: number }) {
  return (
    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
      <span style={{ fontFamily: AF.m, fontSize: 13, color: C.text, fontWeight: 600 }}>{current}</span>
      <span style={{ fontFamily: AF.m, fontSize: 11, color: C.dim }}>/ {total}</span>
    </div>
  )
}

function ModeInstruction({ icon, text }: { icon: string; text: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 24 }}>
      <div style={{ width: 28, height: 28, borderRadius: 7, background: C.accentBg, border: `1px solid ${C.accentBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0 }}>
        {icon}
      </div>
      <span style={{ fontFamily: AF.b, fontSize: 13, color: C.muted }}>{text}</span>
    </div>
  )
}

function KbdRow({ hints }: { hints: [string, string][] }) {
  return (
    <div style={{ display: 'flex', gap: 14, alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', padding: '10px 24px 16px', flexShrink: 0 }}>
      {hints.map(([key, label]) => (
        <span key={key + label} style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
          <Kbd>{key}</Kbd>
          <span style={{ fontFamily: AF.b, fontSize: 11, color: C.dim }}>{label}</span>
        </span>
      ))}
    </div>
  )
}

function ResultScreen({ score, total, onRetry, onExit }: { score: number; total: number; onRetry: () => void; onExit: () => void }) {
  const pct = total > 0 ? Math.round((score / total) * 100) : 0
  const color = pct >= 80 ? 'var(--success)' : pct >= 50 ? 'var(--warn)' : 'var(--error)'
  const R = 48, C2PI = 2 * Math.PI * R
  return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: C.bg }}>
      <div style={{ textAlign: 'center', maxWidth: 380, padding: '0 24px' }}>
        <div style={{ position: 'relative', width: 120, height: 120, margin: '0 auto 28px' }}>
          <svg width="120" height="120" viewBox="0 0 120 120">
            <circle cx="60" cy="60" r={R} fill="none" stroke={C.surf2} strokeWidth="7"/>
            <circle cx="60" cy="60" r={R} fill="none" stroke={color} strokeWidth="7"
              strokeDasharray={C2PI} strokeDashoffset={C2PI * (1 - pct / 100)}
              strokeLinecap="round" transform="rotate(-90 60 60)"
              style={{ transition: 'stroke-dashoffset 1s ease 0.2s' }}/>
          </svg>
          <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ fontFamily: AF.h, fontSize: 26, fontWeight: 700, color, letterSpacing: '-1px', lineHeight: 1 }}>{pct}%</span>
            <span style={{ fontFamily: AF.m, fontSize: 11, color: C.muted }}>{score}/{total}</span>
          </div>
        </div>
        <div style={{ fontFamily: AF.h, fontSize: 26, fontWeight: 700, color: C.text, letterSpacing: '-0.8px', marginBottom: 10 }}>
          {pct === 100 ? 'Perfect.' : pct >= 80 ? 'Well done.' : pct >= 50 ? 'Good effort.' : 'Keep at it.'}
        </div>
        <div style={{ fontFamily: AF.b, fontSize: 15, color: C.muted, marginBottom: 36, lineHeight: 1.6 }}>
          {score} of {total} words recalled.
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          <button onClick={onRetry} style={{ fontFamily: AF.b, fontSize: 14, fontWeight: 700, background: 'var(--accent)', color: C.bg, border: 'none', padding: '12px 28px', borderRadius: 8, cursor: 'pointer' }}>
            Study again
          </button>
          <button onClick={onExit} style={{ fontFamily: AF.b, fontSize: 14, color: C.muted, background: 'transparent', border: `1px solid ${C.border}`, padding: '12px 24px', borderRadius: 8, cursor: 'pointer' }}>
            Choose deck
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Vocabulary exercise — same simple layout as the grammar drill ────────────
function VocabGame({ cards, isMobile, langLabel, onExit }: { cards: VocabCard[]; isMobile: boolean; langLabel: string; onExit: () => void }) {
  const [idx, setIdx]           = useState(0)
  const [revealed, setRevealed] = useState(false)
  const [score, setScore]       = useState(0)
  const [done, setDone]         = useState(false)
  const card = cards[idx]

  const reveal = useCallback(() => setRevealed(true), [])

  const rate = useCallback((known: boolean) => {
    recordVocabularyAttempt({
      term: card.term, timestamp: Date.now(),
      status: known ? 'correct' : 'incorrect', meaning: card.meaning,
    })
    if (known) setScore(s => s + 1)
    if (idx + 1 >= cards.length) { setDone(true); return }
    setIdx(i => i + 1); setRevealed(false)
  }, [idx, cards.length, card])

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (!revealed && (e.key === ' ' || e.key === 'Enter')) { e.preventDefault(); reveal() }
      else if (revealed) {
        if (e.key === '1' || e.key === 'ArrowLeft') rate(false)
        if (e.key === '2' || e.key === 'ArrowRight') rate(true)
      }
    }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [revealed, reveal, rate])

  if (done) return (
    <ResultScreen score={score} total={cards.length} onExit={onExit}
      onRetry={() => { setIdx(0); setRevealed(false); setScore(0); setDone(false) }}/>
  )

  const hp = isMobile ? '20px' : '64px'

  const answers: { known: boolean; label: string; hint: string; color: string; bg: string; border: string }[] = [
    { known: false, label: "Didn't know", hint: '1', color: C.muted,    bg: 'transparent',  border: C.border        },
    { known: true,  label: 'I knew it',   hint: '2', color: C.bg,       bg: 'var(--accent)', border: 'var(--accent)' },
  ]

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: C.bg, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: `12px ${hp} 0` }}>
        <div style={{ flex: 1, height: 3, background: C.border, borderRadius: 2 }}>
          <div style={{ height: '100%', background: 'var(--accent)', width: `${(idx / cards.length) * 100}%`, transition: 'width 0.4s', borderRadius: 2 }}/>
        </div>
        <CounterBadge current={idx + 1} total={cards.length}/>
      </div>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: `24px ${hp}`, maxWidth: 620, width: '100%', alignSelf: 'center' }}>
        <ModeInstruction icon="📖" text={revealed ? 'How well did you know it?' : 'Recall the meaning, then reveal'}/>

        <div style={{ marginBottom: 20 }}>
          <div style={{ fontFamily: AF.h, fontSize: isMobile ? 44 : 68, fontWeight: 700, color: C.text, letterSpacing: '-2.5px', lineHeight: 1.05, marginBottom: 8 }}>
            {card.term}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontFamily: AF.b, fontSize: 13, color: 'var(--accent)', fontWeight: 600 }}>{card.level}</span>
            <span style={{ color: C.border }}>·</span>
            <span style={{ fontFamily: AF.m, fontSize: 12, color: C.muted }}>{langLabel}</span>
          </div>
        </div>

        <div style={{
          borderRadius: 12, border: `2px solid ${C.border2}`, background: C.surf,
          marginBottom: 20, minHeight: isMobile ? 72 : 84,
          display: 'flex', alignItems: 'center', padding: isMobile ? '16px 18px' : '18px 24px',
        }}>
          {revealed ? (
            <div style={{ fontFamily: AF.b, fontSize: isMobile ? 20 : 26, fontWeight: 600, color: C.text, lineHeight: 1.3 }}>{card.meaning}</div>
          ) : (
            <div style={{ fontFamily: AF.b, fontSize: isMobile ? 15 : 16, color: C.dim }}>Reveal to see the meaning</div>
          )}
        </div>

        {!revealed ? (
          <button onClick={reveal} style={{
            alignSelf: 'flex-start', fontFamily: AF.b, fontSize: 14, fontWeight: 700,
            background: 'var(--accent)', color: C.bg, border: 'none',
            padding: '13px 26px', borderRadius: 9, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 9,
          }}>
            Reveal meaning <Kbd>↵</Kbd>
          </button>
        ) : (
          <div className="c8-pop" style={{ display: 'flex', gap: 10, width: '100%' }}>
            {answers.map(({ known, label, hint, color, bg, border }) => (
              <button key={label} onClick={() => rate(known)} style={{
                flex: 1, padding: '15px 12px', borderRadius: 9,
                border: `1px solid ${border}`, background: bg, color,
                fontFamily: AF.b, fontSize: 15, fontWeight: 700, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9,
              }}>
                {label} <Kbd>{hint}</Kbd>
              </button>
            ))}
          </div>
        )}
      </div>

      <KbdRow hints={revealed
        ? [['1 / ←', "Didn't know"], ['2 / →', 'I knew it']]
        : [['Space / ↵', 'reveal']]}/>
    </div>
  )
}

// ── Deck chooser ───────────────────────────────────────────────────────────────
const LEVEL_INFO: Record<string, { label: string; emoji: string; desc: string }> = {
  'A1': { label: 'Beginner',           emoji: '🌱', desc: 'Core everyday words' },
  'A2': { label: 'Elementary',         emoji: '🌿', desc: 'Common conversation' },
  'B1': { label: 'Intermediate',       emoji: '🍀', desc: 'Broader vocabulary' },
  'B2': { label: 'Upper Intermediate', emoji: '🌲', desc: 'Nuanced expression' },
  'C1': { label: 'Advanced',           emoji: '🌳', desc: 'Near-native range' },
  'C2': { label: 'Proficient',         emoji: '🏆', desc: 'Full mastery' },
}

function useIsMobile() {
  const [mobile, setMobile] = useState(false)
  useEffect(() => {
    const check = () => setMobile(window.innerWidth < 768)
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])
  return mobile
}

function DeckChooser({ decks, onStart }: {
  decks: { level: string; cards: VocabCard[] }[];
  onStart: (cards: VocabCard[]) => void;
}) {
  const isMobile = useIsMobile()
  const hp = isMobile ? '16px' : '48px'

  return (
    <div style={{ flex: 1, overflowY: 'auto', background: C.bg, padding: `28px ${hp}` }}>
      <h2 style={{ fontFamily: AF.h, fontSize: 22, fontWeight: 700, color: C.text, margin: '0 0 4px', letterSpacing: '-0.7px' }}>Choose a deck</h2>
      <p style={{ fontFamily: AF.b, fontSize: 14, color: C.muted, margin: '0 0 28px' }}>Select a CEFR level to practise, then recall each word's meaning.</p>

      <div style={{ fontFamily: AF.m, fontSize: 9, color: C.dim, letterSpacing: '0.14em', marginBottom: 12 }}>VOCABULARY DECKS</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {decks.map(({ level, cards }) => {
          const info = LEVEL_INFO[level] ?? { label: level, emoji: '📖', desc: '' }
          return (
            <div key={level} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '16px 20px', borderRadius: 10,
              border: `1px solid ${C.border}`, background: C.surf, flexWrap: 'wrap', gap: 10,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <span style={{ fontSize: 28, lineHeight: 1 }}>{info.emoji}</span>
                <div>
                  <div style={{ fontFamily: AF.h, fontSize: 15, fontWeight: 600, color: C.text, letterSpacing: '-0.3px' }}>{info.label}</div>
                  <div style={{ fontFamily: AF.b, fontSize: 12, color: C.muted, marginTop: 2 }}>{info.desc}</div>
                  <div style={{ fontFamily: AF.m, fontSize: 11, color: C.dim, marginTop: 4 }}>{cards.length} cards</div>
                </div>
              </div>
              <button onClick={() => onStart([...cards].sort(() => Math.random() - 0.5).slice(0, 20))} style={{
                fontFamily: AF.b, fontSize: 13, fontWeight: 700, background: 'var(--accent)', color: C.bg,
                border: 'none', padding: '9px 18px', borderRadius: 7, cursor: 'pointer', flexShrink: 0,
              }}>
                Start →
              </button>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── Page ───────────────────────────────────────────────────────────────────────
export default function Page() {
  const pageContext = usePageContext()
  const langCode = pageContext.routeParams?.language || 'es'
  const comingSoon = pageContext.routeParams?.comingSoon === 'true'
  const languageName = LANG_NAMES[langCode] || 'spanish'
  const motifLang = langCodeToName(langCode)
  const isMobile = useIsMobile()

  const [decks, setDecks] = useState<{ level: string; cards: VocabCard[] }[]>([])
  const [activeCards, setActiveCards] = useState<VocabCard[] | null>(null)

  useEffect(() => {
    const content = getLanguageContent(langCode)
    if (!content) return
    const vocab = content.vocabulary
    const load = typeof vocab === 'function' ? vocab() : Promise.resolve(vocab)
    load.then(cats => {
      const built = cats
        .map(cat => {
          const levelMatch = cat.title.match(/^(A[12]|B[12]|C[12])/)
          if (!levelMatch) return null
          const level = levelMatch[1]
          const cards: VocabCard[] = cat.words.map(w => ({ term: w.term, meaning: w.meaning, level }))
          return { level, cards }
        })
        .filter((d): d is { level: string; cards: VocabCard[] } => d !== null && d.cards.length > 0)
      setDecks(built)
    })
  }, [langCode])

  if (comingSoon) return <ComingSoon language={langCode} />

  const gardenHref = `/learn-${languageName}/garden`

  // Active study session — the same simple layout as the grammar drill.
  if (activeCards) {
    return <VocabGame cards={activeCards} isMobile={isMobile} langLabel={motifLang} onExit={() => setActiveCards(null)} />
  }

  return (
    <div style={{ maxWidth: 640 }}>
      {/* Page header — matches the grammar / practice page style */}
      <div style={{ position: 'relative', overflow: 'hidden', borderBottom: `1px solid ${C.border}` }}>
        <div style={{
          position: 'absolute', inset: 0, opacity: 0.55,
          WebkitMaskImage: 'linear-gradient(to left, #000, transparent 60%)',
          maskImage: 'linear-gradient(to left, #000, transparent 60%)',
        }}>
          <MotifBand language={motifLang} height={112} opacity={0.7} />
        </div>
        <div style={{ position: 'relative', padding: '22px 48px 18px' }}>
          <div style={{ fontFamily: AF.m, fontSize: 10, color: 'var(--accent)', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 8 }}>
            {motifLabel(motifLang)}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <span style={{ fontSize: 18, lineHeight: 1 }}>📚</span>
            <h1 style={{ fontFamily: AF.h, fontSize: 26, fontWeight: 700, color: C.text, margin: 0, letterSpacing: '-0.7px' }}>
              Vocabulary
            </h1>
          </div>
          <p style={{ fontFamily: AF.b, fontSize: 13, color: C.muted, margin: 0 }}>Recall each word's meaning, then rate how well you knew it.</p>
          <a href={gardenHref} style={{
            display: 'inline-flex', alignItems: 'center', gap: 8, marginTop: 13,
            fontFamily: AF.b, fontSize: 12, fontWeight: 700, color: 'var(--accent)',
            background: 'var(--accent-bg)', border: '1px solid var(--accent-border)',
            padding: '7px 13px', borderRadius: 8, textDecoration: 'none',
          }}>🌿 Training for the Conjugarden — take these skills on an expedition →</a>
        </div>
      </div>

      <DeckChooser decks={decks} onStart={cards => setActiveCards(cards)} />
      <div style={{ padding: '0 48px 60px' }}>
        <PageSEOSection pageType="vocabulary" languageName={languageName} />
      </div>
    </div>
  )
}
